import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import axios from 'axios';
import { toast } from 'react-toastify';

const getAuthHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getApiBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const runtimeBase = localStorage.getItem('REACT_APP_API_URL');
    if (runtimeBase && runtimeBase.trim()) {
      return runtimeBase.replace(/\/$/, '').replace(/:(\d+)\/api/i, ':3000/api');
    }
  }
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  }
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname.includes('ondigitalocean.app') ||
      window.location.hostname.includes('services.knls.in'))
  ) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

export default function CoursePackImportDialog({ open, onClose, onImported }) {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setSelectedFile(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    if (importing) return;
    reset();
    onClose?.();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    setError('');
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setError('Choose a .knlcourse or .zip file first');
      return;
    }

    setImporting(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('coursePack', selectedFile);

      const response = await axios.post(
        `${getApiBaseUrl()}/lms/courses/import`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      const restored = response?.data?.data;
      const enrolledCount = Number(restored?.enrolled_students_restored || 0);
      toast.success(
        enrolledCount > 0
          ? `Restored as "${restored?.course_name}" (${restored?.course_code}) with ${enrolledCount} enrolled student(s)`
          : `Restored as "${restored?.course_name}" (${restored?.course_code})`,
      );
      onImported?.(restored);
      reset();
      onClose?.();
    } catch (importError) {
      setError(
        importError?.response?.data?.message ||
          importError.message ||
          'Failed to import course pack',
      );
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import Course Pack</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Alert severity="info">
            Import always creates a <strong>new course</strong> tagged with
            <strong> [Restored]</strong> in the name. Course content and enrolled
            students from the pack are restored together. Existing students are
            matched by phone; missing students are created. Your existing course
            is not overwritten.
          </Alert>

          <Box>
            <input
              ref={fileInputRef}
              type="file"
              accept=".knlcourse,.zip,application/zip"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              id="course-pack-import-input"
            />
            <label htmlFor="course-pack-import-input">
              <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />}>
                Choose pack file
              </Button>
            </label>
            {selectedFile ? (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Selected: {selectedFile.name}
              </Typography>
            ) : null}
          </Box>

          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={importing}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={importing || !selectedFile}
          startIcon={importing ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {importing ? 'Restoring…' : 'Import as new course'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
